import { supabase } from '../lib/supabaseClient';
import type { Event } from '../types/event';

export interface ManualEventRow {
  id: string;
  name: string;
  description: string | null;
  start_time: string;
  end_time: string;
  venue_name: string | null;
  venue_address: string | null;
  image_url: string | null;
  link_url: string;
  button_label: string;
  is_sold_out: boolean;
  created_at: string;
  updated_at: string;
}

export interface ManualEventInput {
  name: string;
  description?: string;
  start_time: string;
  end_time: string;
  venue_name?: string;
  venue_address?: string;
  image_url?: string;
  link_url: string;
  button_label?: string;
  is_sold_out?: boolean;
}

/**
 * Convert a manual_events row into the shape the public event list expects
 */
export function toEvent(row: ManualEventRow): Event {
  return {
    id: `manual-${row.id}`,
    name: row.name,
    description: row.description || '',
    start: row.start_time,
    end: row.end_time,
    venue: {
      name: row.venue_name || 'TBA',
      address: row.venue_address || 'TBA',
    },
    imageUrl: row.image_url || '',
    isSoldOut: row.is_sold_out,
    url: row.link_url,
    buttonLabel: row.button_label,
  };
}

export async function getManualEvents(): Promise<{ data: ManualEventRow[]; error: any }> {
  const { data, error } = await supabase
    .from('manual_events')
    .select('*')
    .order('start_time', { ascending: true });

  if (error) {
    console.error('[Manual Event Service] Error fetching manual events:', error);
  }

  return { data: data || [], error };
}

// Writes go through SECURITY DEFINER RPCs that verify the PIN belongs to an
// admin. The table itself grants anon SELECT only, so the public anon key
// cannot write directly -- see migrations/manual_events_write_access.sql.
function toRpcArgs(input: ManualEventInput, pin: string, id?: string) {
  return {
    p_pin: pin,
    p_name: input.name,
    p_start_time: input.start_time,
    p_end_time: input.end_time,
    p_link_url: input.link_url,
    p_description: input.description || null,
    p_venue_name: input.venue_name || null,
    p_venue_address: input.venue_address || null,
    p_image_url: input.image_url || null,
    p_button_label: input.button_label || 'TICKETS',
    p_is_sold_out: input.is_sold_out || false,
    p_id: id ?? null,
  };
}

// The RPCs raise SQLSTATE 42501 for a bad or non-admin PIN; surface that as
// something an admin can act on rather than the raw Postgres message.
function friendlyError(error: any): any {
  if (error?.code === '42501') {
    return { ...error, message: 'PIN not recognized, or that user is not an admin' };
  }
  return error;
}

export async function createManualEvent(
  input: ManualEventInput,
  pin: string
): Promise<{ data: ManualEventRow | null; error: any }> {
  const { data, error } = await supabase.rpc(
    'admin_upsert_manual_event',
    toRpcArgs(input, pin)
  );

  if (error) {
    console.error('[Manual Event Service] Error creating manual event:', error);
  }

  return { data: data as ManualEventRow | null, error: friendlyError(error) };
}

export async function updateManualEvent(
  id: string,
  input: ManualEventInput,
  pin: string
): Promise<{ data: ManualEventRow | null; error: any }> {
  const { data, error } = await supabase.rpc(
    'admin_upsert_manual_event',
    toRpcArgs(input, pin, id)
  );

  if (error) {
    console.error('[Manual Event Service] Error updating manual event:', error);
  }

  return { data: data as ManualEventRow | null, error: friendlyError(error) };
}

export async function deleteManualEvent(
  id: string,
  pin: string
): Promise<{ error: any }> {
  const { error } = await supabase.rpc('admin_delete_manual_event', {
    p_pin: pin,
    p_id: id,
  });

  if (error) {
    console.error('[Manual Event Service] Error deleting manual event:', error);
  }

  return { error: friendlyError(error) };
}
